import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { PhoneCall, Play, Star } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { TRUST_STATEMENT } from "@/lib/content";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, 160]);

  const scrollToSim = () => document.getElementById("call-sim")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden pt-24 pb-16 bg-white">
      <motion.div
        style={{
          y: yGlow,
          willChange: "transform",
          background: "radial-gradient(circle, rgba(44,62,80,0.09) 0%, rgba(44,62,80,0.05) 35%, rgba(44,62,80,0) 70%)",
        }}
        className="absolute -right-40 top-20 h-[520px] w-[520px] rounded-full pointer-events-none"
      />

      <div className="relative w-full max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-navy-900/10 bg-navy-900/[0.03] px-3 py-1.5 mb-7"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
            </span>
            <span className="text-xs tracking-wide text-navy-900/70">For residential HVAC companies</span>
          </motion.div>

          <h1 className="font-display font-bold tracking-[-0.02em] leading-[1.02] text-[13vw] sm:text-5xl md:text-6xl lg:text-[4.75rem] text-navy-900">
            <MaskLines lines={["Never miss", "another"]} />
            <span className="block overflow-hidden pb-[0.08em]">
              <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 0.34 }}
              >
                HVAC call.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-6 text-lg md:text-xl text-textgray max-w-xl leading-relaxed"
          >
            We help residential HVAC companies stop losing revenue from missed and after‑hours calls by
            installing an AI front desk that answers, qualifies, and books customers 24/7.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.72, duration: 0.7 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/live-demo"
              data-testid="hero-book-btn"
              className="group inline-flex items-center justify-center gap-2 h-13 px-7 py-4 rounded-sm bg-navy-900 hover:bg-navy-950 text-white font-semibold transition-all duration-300"
            >
              <PhoneCall className="w-4 h-4" /> Try the AI Dispatcher Live
            </Link>
            <button
              onClick={scrollToSim}
              data-testid="hero-hear-btn"
              className="inline-flex items-center justify-center gap-2 h-13 px-7 py-4 rounded-sm border border-navy-900/20 hover:border-navy-900/50 hover:bg-navy-900/5 text-navy-900 font-medium transition-colors duration-300"
            >
              <Play className="w-4 h-4 fill-current" /> Hear Her Take a Call
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-12"
          >
            <div className="flex items-center gap-2 text-xs text-textgray mb-3">
              <div className="flex text-navy-900">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
              </div>
              Turn every call into a booked opportunity.
            </div>
            <p className="text-sm font-display font-semibold text-textlight">{TRUST_STATEMENT}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
